import {
  useSaveNotificationPreference,
  useGetNotificationPreference,
  type GetHirerNotificationPreferenceResult,
  HirerNotificationPreferenceForm,
} from '@seek/hirer-notification-preference';
import { useTranslations } from '@vocab/react';
import {
  Button,
  Stack,
  Actions,
  Notice,
  Text,
  TextLink,
} from 'braid-design-system';
import { useEffect, useState, type FC, type PropsWithChildren } from 'react';

import { ACTION_RESULT } from 'src/App/constants/submitActions';
import { useConfig } from 'src/hooks/useConfig';
import { getPhoneNumber } from 'src/modules/localisation/customerService';
import { isSiteAnz } from 'src/siteConfig';
import { getContactUsUrl } from 'src/utils/routes';

import translations from './.vocab';
import { ErrorNotice } from './ErrorNotice';

interface Props {
  onSubmitSuccess(): void;
  onCancel(): void;
}

interface NotificationPreferenceFormState {
  isServerError: boolean;
  error: string; // todo: figure out error message localisation with server
  hasLoadError: boolean;
  notificationPreferenceLoaded: boolean;
  actionResult: ACTION_RESULT;
}

export const NotificationPreferenceForm: FC<PropsWithChildren<Props>> = ({
  onSubmitSuccess,
  onCancel,
}: Props) => {
  const saveNotificationPreference = useSaveNotificationPreference();
  const getNotificationPreference = useGetNotificationPreference();

  const [state, setState] = useState<NotificationPreferenceFormState>({
    isServerError: false,
    error: '',
    hasLoadError: false,
    notificationPreferenceLoaded: false,
    actionResult: ACTION_RESULT.NONE,
  });

  const { site, urlResolver } = useConfig();
  const { t } = useTranslations(translations);

  const isAnz = isSiteAnz(site);
  const csPhoneNumber = getPhoneNumber(site);
  const contactUsUrl = getContactUsUrl(urlResolver);

  useEffect(() => {
    if (!state.notificationPreferenceLoaded) {
      getNotificationPreference(
        (
          notificationPreferenceResult: GetHirerNotificationPreferenceResult,
        ) => {
          setState((prevState) => ({
            ...prevState,
            notificationPreferenceLoaded: true,
            hasLoadError: Boolean(notificationPreferenceResult.error),
          }));
        },
      );
    }
  }, [state.notificationPreferenceLoaded, getNotificationPreference]);

  const onSave = async () => {
    setState((prevState) => ({
      ...prevState,
      actionResult: ACTION_RESULT.LOADING,
    }));
    const saveResult = await saveNotificationPreference();
    if (saveResult.success) {
      setState((prevState) => ({
        ...prevState,
        actionResult: ACTION_RESULT.SUCCESS,
      }));
      onSubmitSuccess();
    } else {
      const errorMessage = saveResult.error?.errorMessage ?? '';
      const isServerError = saveResult?.error?.isServerError ?? false;
      setState((prevState) => ({
        ...prevState,
        actionResult: ACTION_RESULT.ERROR,
        isServerError,
        error: errorMessage,
      }));
    }
  };

  const activeButtons = (
    <>
      <Button onClick={onSave}>{t('Save')}</Button>
      <Button variant="transparent" onClick={onCancel}>
        {t('Cancel')}
      </Button>
    </>
  );

  const inactiveButtons = <Button loading>{t('Saving')}</Button>;

  const AnzMessage = () => (
    <Text tone="critical">
      {t('Sorry we are having trouble displaying...anz', {
        CustomerServicePhoneNumber: () => (
          <TextLink key="csphone" href={`tel:${csPhoneNumber}`}>
            {csPhoneNumber}
          </TextLink>
        ),
      })}
    </Text>
  );

  const AsiaMessage = () => (
    <Text tone="critical">
      {t('Sorry we are having trouble displaying...asia', {
        Link: (children) => (
          <TextLink key="cslink" href={`${contactUsUrl}`}>
            {children}
          </TextLink>
        ),
      })}
    </Text>
  );

  return state.hasLoadError ? (
    <Notice tone="critical">{isAnz ? <AnzMessage /> : <AsiaMessage />}</Notice>
  ) : (
    <Stack space="medium">
      <HirerNotificationPreferenceForm id="user-preferences-form" />
      <ErrorNotice isServerError={state.isServerError} error={state.error} />
      <Actions>
        {ACTION_RESULT.LOADING === state.actionResult
          ? inactiveButtons
          : activeButtons}
      </Actions>
    </Stack>
  );
};
